import React, { useState } from 'react';
import BottomSheet from '../../ui/BottomSheet';

/**
 * Mid-trip add-on charges (02-trip-lifecycle.md "Add-on charges"). The app is a
 * controlled input surface, not a pricing engine — server's PRICE handler
 * (lib/dispatch.js jobUpdatePriceItems) only UPDATEs pre-existing Trip_Price
 * rows matched by id, it cannot create a brand-new rate item on the fly. So:
 *
 *  - "Extras" here are the trip's own rate-card `pricing[]` rows (type==='each'),
 *    the same ones FareConfirmationModal already offers at trip-end — this sheet
 *    just lets the driver set them earlier, mid-trip. Quantities chosen here are
 *    merged into FareConfirmationModal's defaults at trip completion (see
 *    LiveDispatch's mergedExtras/mergedDispositionPrice), not sent immediately —
 *    avoids racing the PRICE-before-COMPM ack sequence used at trip end.
 *  - "Tolls / Parking / Other fee" are amounts the driver enters against the
 *    trip's disposition.toll / disposition.surcharge fields (same fields the
 *    (currently hidden) FareConfirmationModal fields would have written to) —
 *    merged in the same way.
 *  - "Add Stop" has no server endpoint yet to insert a new leg/reprice — this
 *    sends a structured note to dispatch via the existing chat channel instead
 *    of pretending to reprice automatically.
 *  - "Wait Time" is already tracked automatically by the soft meter; shown
 *    read-only here rather than duplicated with a manual timer.
 */
function AddOnChargesSheet({
  open,
  onClose,
  waypoint,
  extras = [],
  pendingExtraQty = {},
  onChangeExtraQty,
  pendingToll = 0,
  pendingSurcharge = 0,
  onChangeToll,
  onChangeSurcharge,
  feeOptions = ['Toll', 'Parking', 'Other Approved Fee'],
  softMeterState,
  onSendStopRequest,
}) {
  const [stopAddress, setStopAddress] = useState('');
  const [stopSent, setStopSent] = useState(false);
  const [feeReason, setFeeReason] = useState(feeOptions[0] || 'Toll');
  const [feeAmount, setFeeAmount] = useState('');

  const handleSendStop = () => {
    const addr = stopAddress.trim();
    if (!addr) return;
    onSendStopRequest?.(addr);
    setStopSent(true);
    setStopAddress('');
    setTimeout(() => setStopSent(false), 4000);
  };

  const handleAddFee = () => {
    const amt = Number(feeAmount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    if (feeReason === 'Toll' || feeReason === 'Parking') {
      onChangeToll?.(roundTwo(pendingToll + amt));
    } else {
      onChangeSurcharge?.(roundTwo(pendingSurcharge + amt));
    }
    setFeeAmount('');
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Add-On Charges">
      <div className="space-y-5">
        {/* Add Stop */}
        <section>
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Add Stop</h4>
          <p className="text-xs text-gray-500 mb-2">
            Sends a request to dispatch — they'll confirm the stop and any added charge.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={stopAddress}
              onChange={(e) => setStopAddress(e.target.value)}
              placeholder="Stop address"
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={handleSendStop}
              disabled={!stopAddress.trim()}
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              Send
            </button>
          </div>
          {stopSent && <p className="text-xs text-emerald-600 mt-1">Sent to dispatch.</p>}
        </section>

        {/* Wait Time — read only, already automatic */}
        {softMeterState?.isRunning && (
          <section>
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Wait Time</h4>
            <div className="flex justify-between text-sm bg-gray-50 rounded px-3 py-2">
              <span className="text-gray-600">Tracked automatically by the meter</span>
              <span className="font-mono">${Number(softMeterState.waitCharge || 0).toFixed(2)}</span>
            </div>
          </section>
        )}

        {/* Extras (rate-card each-type items) */}
        {extras.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Extras</h4>
            <div className="space-y-2">
              {extras.map((x, i) => {
                const k = String(x.id || `idx_${i}`);
                const q = Number(pendingExtraQty[k]) || 0;
                const rate = Number(x.rate) || 0;
                return (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <div className="flex flex-col flex-1 mr-2">
                      <span>{x.name || 'Extra'}</span>
                      <span className="text-xs text-gray-500 font-mono">${rate.toFixed(2)} each</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 font-bold"
                        onClick={() => onChangeExtraQty?.(k, Math.max(0, q - 1))}
                        disabled={q <= 0}
                      >
                        −
                      </button>
                      <span className="w-6 text-center font-mono">{q}</span>
                      <button
                        type="button"
                        className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 font-bold"
                        onClick={() => onChangeExtraQty?.(k, q + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Tolls / Parking / Fees — company-defined reasons only */}
        <section>
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Tolls / Parking / Fees</h4>
          <div className="flex gap-2 mb-2">
            <select
              value={feeReason}
              onChange={(e) => setFeeReason(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1"
            >
              {feeOptions.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              min="0"
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
              placeholder="$0.00"
              className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm text-right font-mono"
            />
            <button
              type="button"
              onClick={handleAddFee}
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-semibold"
            >
              Add
            </button>
          </div>
          <div className="text-xs text-gray-600 space-y-0.5">
            {pendingToll > 0 && <div>Tolls/Parking recorded: ${pendingToll.toFixed(2)}</div>}
            {pendingSurcharge > 0 && <div>Other fees recorded: ${pendingSurcharge.toFixed(2)}</div>}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Applied to the fare when the trip is completed.
          </p>
        </section>
      </div>
    </BottomSheet>
  );
}

function roundTwo(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default AddOnChargesSheet;
